import React, { ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface PermissionGateProps {
  /**
   * Single permission or array of permissions required to show children
   */
  permission?: string | string[];

  /**
   * If true and multiple permissions are provided, user must have ALL permissions.
   * If false (default), user only needs ANY of the permissions.
   */
  requireAll?: boolean;

  /**
   * Alternative to permissions: check specific role(s)
   */
  role?: string | string[];

  /**
   * If true and multiple roles are provided, user must have ALL roles.
   * If false (default), user only needs ANY of the roles.
   */
  requireAllRoles?: boolean;

  /**
   * Content to show if user HAS required permissions
   */
  children: ReactNode;

  /**
   * Optional fallback content to show if user LACKS required permissions
   */
  fallback?: ReactNode;

  /**
   * Optional callback when permission check fails
   */
  onPermissionDenied?: () => void;
}

/**
 * PermissionGate - Conditionally renders children based on user permissions
 * 
 * @example
 * ```tsx
 * // Single permission
 * <PermissionGate permission="companies:view">
 *   <ViewButton />
 * </PermissionGate>
 * 
 * // Multiple permissions (ANY)
 * <PermissionGate permission={['companies:view', 'companies:manage']}>
 *   <DataTable />
 * </PermissionGate>
 * 
 * // Multiple permissions (ALL required)
 * <PermissionGate permission={['companies:view', 'companies:export']} requireAll>
 *   <ExportButton />
 * </PermissionGate>
 * 
 * // With fallback
 * <PermissionGate 
 *   permission="companies:delete"
 *   fallback={<Tooltip title="No permission"><span><Button disabled>Delete</Button></span></Tooltip>}
 * >
 *   <Button>Delete</Button>
 * </PermissionGate>
 * 
 * // Role-based (backwards compatible)
 * <PermissionGate role="SUPER_ADMIN">
 *   <AdminPanel />
 * </PermissionGate>
 * ```
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  requireAll = false,
  role,
  requireAllRoles = false,
  children,
  fallback = null,
  onPermissionDenied,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole, hasAllRoles } = useAuth();

  // Determine if user has required permissions
  let hasAccess = true;

  // Check permissions first
  if (permission) {
    if (Array.isArray(permission)) {
      hasAccess = requireAll 
        ? hasAllPermissions(permission) 
        : hasAnyPermission(permission);
    } else {
      hasAccess = hasPermission(permission);
    }
  }

  // Check roles (if no permission check or as additional check)
  if (hasAccess && role) {
    if (Array.isArray(role)) {
      hasAccess = requireAllRoles 
        ? hasAllRoles(role) 
        : hasAnyRole(role);
    } else {
      hasAccess = hasRole(role);
    }
  }

  // If neither permission nor role is specified, show children by default
  if (!permission && !role) {
    hasAccess = true;
  }

  // Call callback if permission denied
  if (!hasAccess && onPermissionDenied) {
    onPermissionDenied();
  }

  return <>{hasAccess ? children : fallback}</>;
};

export default PermissionGate;

